import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import {
    makeStyles,
    Menu,
    MenuItem,
    MenuItemProps,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Overflow,
    OverflowItem,
    OverflowItemProps,
    tokens,
    Toolbar,
    ToolbarButton,
    ToolbarButtonProps,
    ToolbarDivider,
    ToolbarProps,
    Tooltip,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components'
import type { ObserveOptions } from '@fluentui/priority-overflow'
import { useTranslation } from 'react-i18next'
import { bundleIcon, MoreVerticalFilled, MoreVerticalRegular } from '@fluentui/react-icons'

export const useOverflowToolbarPanelStyles = makeStyles({
    selectedMenuItem: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
    },
})

const MoreIcon = bundleIcon(MoreVerticalFilled, MoreVerticalRegular)

export type HammockToolbarProps = Pick<ToolbarProps, 'className' | 'size' | 'checkedValues' | 'onCheckedValueChange'>

export type HammockToolbarButtonData = Omit<ToolbarButtonProps, 'onClick'> & {
    value: string
    titleKey?: string
    route?: string
    onClick?: (value: string) => void
    filter?: (button: HammockToolbarButtonData) => boolean
    disabled?: boolean
    render?: (button: HammockToolbarButtonData, key: string | number) => ReactElement
    divider?: boolean
    childButtons?: HammockToolbarButtonData[]
}

type OverflowToolbarPanelProps = Partial<ObserveOptions> & {
    containerClassName?: string
    buttons: HammockToolbarButtonData[]
    filter?: (buttons: HammockToolbarButtonData[]) => HammockToolbarButtonData[]
    overflowButtonLabelKey: string

    toolbarProps?: HammockToolbarProps
    getToolbarButtonProps?: (button?: HammockToolbarButtonData) => ToolbarButtonProps
    getMenuItemProps?: (button: HammockToolbarButtonData) => MenuItemProps
}

type OverflowToolbarItemProps = OverflowToolbarPanelProps & {
    index: number
    button: HammockToolbarButtonData
}

type OverflowMenuItemProps = OverflowToolbarPanelProps &
    Pick<OverflowItemProps, 'id'> & {
        button: HammockToolbarButtonData
    }

function OverflowMenuItem(props: OverflowMenuItemProps): ReactElement {
    const { t: translate } = useTranslation()
    const isVisible = useIsOverflowItemVisible(props.id)

    if (isVisible) {
        return null
    }

    return (
        <MenuItem {...props.getMenuItemProps?.(props.button)} icon={props.button.icon} onClick={() => props.button.onClick?.(props.button.value)}>
            {props.button.title ?? translate(props.button.titleKey)}
        </MenuItem>
    )
}

function OverflowMenu(props: OverflowToolbarPanelProps): ReactElement {
    const { t: translate } = useTranslation()
    const { ref, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>()

    const buttons = useMemo(() => {
        const list = (props.buttons || []).reduce((a, b) => [...a, ...(b.childButtons || [b])], [] as HammockToolbarButtonData[])
        return props.filter?.(list) || list || []
    }, [props.buttons])

    if (!isOverflowing) {
        return null
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={`+${translate(props.overflowButtonLabelKey, { count: overflowCount })}`}>
                    <ToolbarButton key={'-1'} ref={ref} {...props.getToolbarButtonProps?.()} icon={<MoreIcon />} />
                </Tooltip>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    {buttons.map((button, index) => (
                        <OverflowMenuItem key={index} id={`${index}`} button={button} {...props} />
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

function OverflowToolbarItemMenu(props: OverflowToolbarItemProps): ReactElement {
    const { t: translate } = useTranslation()

    const childButtons = useMemo(() => props.filter?.(props.button.childButtons) || props.button.childButtons || [], [props.button])

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={props.button.title ?? translate(props.button.titleKey)}>
                    <ToolbarButton key={props.index} {...props.getToolbarButtonProps?.(props.button)} icon={props.button.icon} />
                </Tooltip>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    {childButtons.map((button, index) =>
                        button.render ? (
                            button.render(button, `${props.index}-${index}`)
                        ) : (
                            <MenuItem
                                key={`${props.index}-${index}`}
                                {...props.getMenuItemProps?.(button)}
                                icon={button.icon}
                                onClick={() => button.onClick(button.value)}
                            >
                                {button.title ?? translate(button.titleKey)}
                            </MenuItem>
                        ),
                    )}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

function OverflowToolbarItem(props: OverflowToolbarItemProps): ReactElement {
    const { t: translate } = useTranslation()

    const childButtons = useMemo(() => props.filter?.(props.button.childButtons) || props.button.childButtons || [], [props.button])

    return (
        <OverflowItem key={props.index} id={`${props.index}`}>
            {props.button.divider ? (
                <ToolbarDivider />
            ) : props.button.render ? (
                props.button.render(props.button, props.index)
            ) : (
                <Tooltip key={props.index} relationship={'label'} content={props.button.title ?? translate(props.button.titleKey)}>
                    {childButtons.length > 0 ? (
                        <OverflowToolbarItemMenu {...props} />
                    ) : (
                        <ToolbarButton
                            key={props.index}
                            {...props.getToolbarButtonProps?.(props.button)}
                            icon={props.button.icon}
                            onClick={() => props.button.onClick?.(props.button.value)}
                        />
                    )}
                </Tooltip>
            )}
        </OverflowItem>
    )
}

/**
 * Renders an Overflow for a Toolbar. All ToolbarButtons are wrapped so they can function as overflow button.
 * When a ToolbarButton contains children, it functions as Menu button.
 *
 * @param props
 * @constructor
 */
export default function OverflowToolbarPanel(props: OverflowToolbarPanelProps): ReactElement {
    return (
        <Overflow minimumVisible={props.minimumVisible ?? 3}>
            <div className={props.containerClassName}>
                <Toolbar {...props.toolbarProps}>
                    <>
                        {(props.filter?.(props.buttons) || props.buttons)?.map((button, index) => (
                            <OverflowToolbarItem key={index} index={index} button={button} {...props} />
                        ))}
                        <OverflowMenu {...props} />
                    </>
                </Toolbar>
            </div>
        </Overflow>
    )
}
